// How much of a credit code (a referral code, a reward code, or a plain
// credit code) the price summary may show off this basket, in pence.
// Imports nothing, so node --test can run it (promo-credit.test.mjs).
//
// A code with a minimum basket (some promo codes; referral codes have had
// none since 26 Oct 2026): under it the code is worth nothing here, as the server
// will refuse it when the order is priced. Percent codes are not credit.
export const codeCreditCents = (promoCodeInfo, basketCents) => {
  const { type, value = 0, min_basket: minBasket } = promoCodeInfo || {}
  if (!/^CreditPromoCode/.test(type || '')) return 0
  if (minBasket && basketCents < minBasket * 100) return 0
  return Math.round(value * 100)
}

// The summary line for it: "£25 off, from Robin" (the server sends only
// the referrer's first name), else "£25 off". No em dashes in customer copy.
export const codeLabel = (promoCodeInfo, formatted) => {
  const from = promoCodeInfo?.from
  return from ? `${formatted} off, from ${from}` : `${formatted} off`
}

// Shown while the basket is under the code's floor.
export const codeFloorHint = (promoCodeInfo, basketCents, formatValue, formatFloor) => {
  const { type, min_basket: minBasket } = promoCodeInfo || {}
  if (!/^CreditPromoCode/.test(type || '') || !minBasket || basketCents >= minBasket * 100) return null
  return `Your ${formatValue} code applies to washes from ${formatFloor}`
}

// The pay press's draft (embedded_checkout, or the hosted fallback's
// checkout) refused the code for who is booking: their own code, a friend
// who has booked before, a second use. The page drops the code, so the
// total it shows is the one the card will pay, and says why.
export const promoRefusedAtPay = res => res?.status === 'error' && res?.data?.field === 'promo_code'

// Why a code was refused, as the server says it (26 Oct 2026): the sentence
// for the customer and a stable key (PromoCode::REFUSAL_REASONS) for the
// page and analytics, the same keys promo_code_rejected sends (ANALYTICS.md).
// Shown under the code field, never as money off.
export const PROMO_REFUSAL_REASONS = ['not_found', 'disabled', 'not_started', 'expired', 'used_up', 'already_used',
  'min_basket', 'own_code', 'existing_customer', 'already_referred', 'cross_referral', 'unknown']
export const UNKNOWN_CODE = "We don't recognise that code."

// The pay press's refusal (promoRefusedAtPay) carries the plain sentence as
// data.refusal_message; its message adds "We have taken it off..." for the
// pay area. `code`: the code the page held or typed (the server never
// sends it back).
export const promoRefusal = (json, code) => {
  const key = json?.data?.reason || json?.data?.refusal
  const reason = PROMO_REFUSAL_REASONS.includes(key) ? key : (json ? 'unknown' : 'not_found')
  const said = json?.data?.refusal_message || json?.message
  const message = (typeof said === 'string' && said.trim()) || UNKNOWN_CODE
  return { code: ((code || '') + '').trim().toLowerCase(), reason, message }
}

// The one line under the code field: a refusal, else a held code that this
// basket is under the floor of (it shows no money off until the basket
// reaches it). Null for nothing to say.
export const promoFieldNotice = (refusal, promoCodeInfo, basketCents) => {
  if (refusal?.message) return { reason: refusal.reason, message: refusal.message }
  const { type, min_basket: minBasket } = promoCodeInfo || {}
  if (!/^CreditPromoCode/.test(type || '') || !minBasket || basketCents >= minBasket * 100) return null
  return { reason: 'min_basket', message: `This code is for washes from £${minBasket}` }
}
